import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PaymentIcon from '@mui/icons-material/Payment';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import LogoutIcon from '@mui/icons-material/Logout';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useAuthStore } from '../../stores/authStore';
import { useThemeMode } from '../../theme';
import { useFlag } from '../../lib/flags';
import type { ThemeMode } from '../../stores/themeStore';

export const SIDEBAR_WIDTH = 240;

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  variant?: 'permanent' | 'temporary';
}

const THEME_CYCLE: Record<ThemeMode, ThemeMode> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

function ThemeIcon({ mode }: { mode: ThemeMode }) {
  if (mode === 'light') return <LightModeIcon />;
  if (mode === 'dark') return <DarkModeIcon />;
  return <SettingsBrightnessIcon />;
}

function ThemeLabel({ mode }: { mode: ThemeMode }) {
  if (mode === 'light') return 'Light mode';
  if (mode === 'dark') return 'Dark mode';
  return 'System mode';
}

export function Sidebar({ mobileOpen = false, onMobileClose, variant = 'permanent' }: SidebarProps) {
  const theme = useTheme();
  const { user, isAuthenticated, signOut } = useAuthStore();
  const [themeMode, setThemeMode] = useThemeMode();
  const newDashboard = useFlag('new-dashboard');
  const navigate = useNavigate();

  const isAdmin = isAuthenticated && user?.role === 'admin';

  function handleSignOut() {
    signOut().then(() => {
      navigate('/');
    });
  }

  function handleThemeCycle() {
    setThemeMode(THEME_CYCLE[themeMode]);
  }

  const avatarLabel = user?.name
    ? user.name.charAt(0).toUpperCase()
    : user?.email
      ? user.email.charAt(0).toUpperCase()
      : '?';

  const navItemSx = (isActive: boolean) => ({
    borderRadius: 1,
    mx: 1,
    mb: 0.5,
    color: isActive ? 'primary.main' : 'text.primary',
    bgcolor: isActive ? `${theme.palette.primary.main}1F` : 'transparent',
    '&:hover': {
      bgcolor: isActive
        ? `${theme.palette.primary.main}29`
        : 'action.hover',
    },
  });

  const navIconSx = (isActive: boolean) => ({
    color: isActive ? 'primary.main' : 'text.secondary',
    minWidth: 40,
  });

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: SIDEBAR_WIDTH,
      }}
    >
      {/* Logo / App name */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 2.5,
        }}
      >
        <RocketLaunchIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h6" fontWeight="bold" noWrap>
          SaaS App
        </Typography>
      </Box>

      <Divider />

      {/* Navigation links */}
      <List sx={{ flex: 1, pt: 1 }}>
        {/* Home — always visible */}
        <ListItem disablePadding>
          <NavLink to="/" end style={{ width: '100%', textDecoration: 'none', color: 'inherit' }}>
            {({ isActive }) => (
              <ListItemButton sx={navItemSx(isActive)}>
                <ListItemIcon sx={navIconSx(isActive)}>
                  <HomeIcon />
                </ListItemIcon>
                <ListItemText primary="Home" />
              </ListItemButton>
            )}
          </NavLink>
        </ListItem>

        {/* Authenticated-only nav items */}
        {isAuthenticated && (
          <>
            <Divider sx={{ my: 1, mx: 2 }} />

            {/* Dashboard */}
            <ListItem disablePadding>
              <NavLink to="/dashboard" style={{ width: '100%', textDecoration: 'none', color: 'inherit' }}>
                {({ isActive }) => (
                  <ListItemButton sx={navItemSx(isActive)}>
                    <ListItemIcon sx={navIconSx(isActive)}>
                      <DashboardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard" />
                    {newDashboard && (
                      <Chip label="New" color="primary" size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                    )}
                  </ListItemButton>
                )}
              </NavLink>
            </ListItem>

            {/* Billing */}
            <ListItem disablePadding>
              <NavLink to="/billing" style={{ width: '100%', textDecoration: 'none', color: 'inherit' }}>
                {({ isActive }) => (
                  <ListItemButton sx={navItemSx(isActive)}>
                    <ListItemIcon sx={navIconSx(isActive)}>
                      <PaymentIcon />
                    </ListItemIcon>
                    <ListItemText primary="Billing" />
                  </ListItemButton>
                )}
              </NavLink>
            </ListItem>

            {/* Security */}
            <ListItem disablePadding>
              <NavLink to="/settings/security" style={{ width: '100%', textDecoration: 'none', color: 'inherit' }}>
                {({ isActive }) => (
                  <ListItemButton sx={navItemSx(isActive)}>
                    <ListItemIcon sx={navIconSx(isActive)}>
                      <SecurityIcon />
                    </ListItemIcon>
                    <ListItemText primary="Security" />
                  </ListItemButton>
                )}
              </NavLink>
            </ListItem>

            {/* Admin — only for admin role */}
            {isAdmin && (
              <>
                <Divider sx={{ my: 1, mx: 2 }} />
                <ListItem disablePadding>
                  <NavLink to="/admin" style={{ width: '100%', textDecoration: 'none', color: 'inherit' }}>
                    {({ isActive }) => (
                      <ListItemButton sx={navItemSx(isActive)}>
                        <ListItemIcon sx={navIconSx(isActive)}>
                          <AdminPanelSettingsIcon />
                        </ListItemIcon>
                        <ListItemText primary="Admin" />
                      </ListItemButton>
                    )}
                  </NavLink>
                </ListItem>
              </>
            )}
          </>
        )}
      </List>

      <Divider />

      {/* Bottom section */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Theme toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={`Switch theme (current: ${ThemeLabel({ mode: themeMode })})`}>
            <IconButton size="small" onClick={handleThemeCycle} color="default">
              <ThemeIcon mode={themeMode} />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            <ThemeLabel mode={themeMode} />
          </Typography>
        </Box>

        {/* Auth section */}
        {isAuthenticated ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                {avatarLabel}
              </Avatar>
              <Typography variant="body2" noWrap sx={{ flex: 1 }} color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={handleSignOut}
              fullWidth
              color="inherit"
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              Sign out
            </Button>
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={() => navigate('/login')}
              sx={{ textTransform: 'none' }}
            >
              Login
            </Button>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => navigate('/register')}
              sx={{ textTransform: 'none' }}
            >
              Register
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );

  if (variant === 'temporary') {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
